import { useEffect, useMemo, useRef, useState } from "react";
import { PlayerRef } from "@remotion/player";
import { getVideoMetadata } from "@remotion/media-utils";
import PlayerOnly from "./Player-only";
import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import {
  GetFps,
  GetLinkedToPreviewActionId,
  GetPreviewDuration,
  GetPreviewSrc,
  GetTimelineActions,
  SetInAndOutFrame,
  SetPreviewDuration,
} from "../../Redux/Reducers/redux-slice-timetool";
import { useDispatch } from "react-redux";
import { cacheService } from "../../Core/Api/services";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export const ControlledPlayer = () => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("Hello World");
  const [color, setColor] = useState("#ffffff");
  const [bgColor, setBgColor] = useState("#000000");
  const [loop, setLoop] = useState(false);
  const [doubleClickToFullscreen, setDoubleClickToFullscreen] = useState(true);
  const [clickToPlay, setClickToPlay] = useState(true);
  const [spaceKeyToPlayOrPause, setSpaceKeyToPlayOrPause] = useState(true);
  const [moveToBeginningWhenEnded, setMoveToBeginningWhenEnded] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPosterWhenUnplayed, setshowPosterWhenUnplayed] = useState(true);
  const [showPosterWhenEnded, setShowPosterWhenEnded] = useState(true);
  const [showPosterWhenPaused, setShowPosterWhenPaused] = useState(true);
  const [alwaysShowControls, setAlwaysShowControls] = useState(false);
  const [showVolumeControls, setShowVolumeControls] = useState(true);
  const [showPlaybackRateControl, setPlaybackRateControl] = useState(false);
  const [width, setWidth] = useState(1080);
  const [height, setHeight] = useState(680);
  const fps = useSelector(GetFps);

  const duration = useSelector(GetPreviewDuration);
  const src = useSelector(GetPreviewSrc);
  const linkedAction = useSelector(GetLinkedToPreviewActionId);
  const timelineActionsFlat = useSelector(GetTimelineActions)
    .map((x) => x.actions)
    .flat(1);

  const dispatch = useDispatch();
  const customSnack = useCustomSnackbar();

  const ref = useRef<PlayerRef>(null);

  useEffect(() => {
    src !== "" &&
      getVideoMetadata(`${src}/${cacheService.GetAuthKey()}`)
        .then(({ durationInSeconds, width, height }) => {
          setWidth(width);
          setHeight(height);
          dispatch(SetPreviewDuration(Math.round(durationInSeconds * fps)));
          if (linkedAction !== "") {
            const foundAction = timelineActionsFlat.find((x) => x.id?.toString() === linkedAction);
            if (!foundAction) return;
            dispatch(
              SetInAndOutFrame({
                inFrame: Math.round(foundAction.data.offSet),
                outFrame: Math.min(
                  Math.round((foundAction.end - foundAction.start) * fps + foundAction.data.offSet),
                  Math.round(durationInSeconds * fps)
                ),
              })
            );
          } else {
            dispatch(SetInAndOutFrame({ inFrame: 0, outFrame: 1 }));
          }
        })
        .catch((err) => {
          customSnack(t("alert:error.fetchingMetadata", { error: err }), "error");
        });
  }, [src, linkedAction]);

  const inputProps = useMemo(() => {
    return {
      title: String(title),
      bgColor: String(bgColor),
      color: String(color),
    };
  }, [bgColor, color, title]);

  const NoPreviewMemo = useMemo(
    () => (
      <Stack
        sx={{ position: "relative", width: "100%" }}
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        height={"100%"}
      >
        <Typography variant="h5" component="h2">
          {t("page:body.noVideoToPreview")}
        </Typography>
        <Typography mt={1} component="p" color="text.secondary">
          {t("page:body.selectAssetTopLeftInstruction")}
        </Typography>
      </Stack>
    ),
    []
  );
  if (src && src !== "")
    return (
      <PlayerOnly
        controls={true}
        alwaysShowControls={alwaysShowControls}
        clickToPlay={clickToPlay}
        doubleClickToFullscreen={doubleClickToFullscreen}
        durationInFrames={duration}
        inputProps={inputProps}
        loop={loop}
        moveToBeginningWhenEnded={moveToBeginningWhenEnded}
        playbackRate={playbackRate}
        spaceKeyToPlayOrPause={spaceKeyToPlayOrPause}
        playerRef={ref}
        showPosterWhenEnded={showPosterWhenEnded}
        showPosterWhenPaused={showPosterWhenPaused}
        showPosterWhenUnplayed={showPosterWhenUnplayed}
        showVolumeControls={showVolumeControls}
        showPlaybackRateControl={showPlaybackRateControl}
        width={width}
        height={height}
        fps={fps}
        context="preview"
      />
    );

  return NoPreviewMemo;
};
